import React, { useState, useEffect } from "react";
import {
  Popover, Box, TextField, Button, Typography, IconButton, Divider,
} from "@material-ui/core";
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from "@material-ui/icons";
import Select from "react-select";
import { useRegions } from "../../hooks/useRegions";
import { useJobProfilesByRegion } from "../../hooks/useJobProfilesByRegion";
import { useCreateSkillDimension } from "../../hooks/useCreateSkillDimension";
import { useUpdateSkillDimension } from "../../hooks/useUpdateSkillDimension";
import { useUpdateSkillDimensionChildren } from "../../hooks/useUpdateSkillDimensionChildren";
import ImageUpload from "../ChallengeBuilder/ImageUpload";
import Swal from "sweetalert2";

const UnitOfCompetenceModal = ({
  open,
  onClose,
  isUpdate: isUpdateProp,
  item,
  defaultRegion,
  defaultJobProfile,
}) => {
  const [name, setName] = useState("");
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedJobProfile, setSelectedJobProfile] = useState(null);
  const [badgeUrl, setBadgeUrl] = useState("");
  const [threshold, setThreshold] = useState("");

  const [savedKey, setSavedKey] = useState(null);
  const isEditMode = isUpdateProp || savedKey !== null;

  const [children, setChildren] = useState([]);
  const [newChildName, setNewChildName] = useState("");
  const [editingChildIndex, setEditingChildIndex] = useState(null);

  const { data: regionsData } = useRegions();
  const { data: jobProfilesData } = useJobProfilesByRegion(selectedRegion?.value);
  const createMutation = useCreateSkillDimension();
  const updateMutation = useUpdateSkillDimension();
  const childrenMutation = useUpdateSkillDimensionChildren();

  const regions = regionsData?.regions || [];
  const jobProfiles = jobProfilesData?.jobProfiles || [];

  const regionOptions = regions.map((r) => ({
    value: r.key || r._id,
    label: r.name,
  }));

  const jobProfileOptions = jobProfiles.map((jp) => ({
    value: jp.key || jp._id,
    label: jp.name,
  }));

  useEffect(() => {
    if (!isUpdateProp) {
      setSelectedJobProfile(null);
    }
  }, [selectedRegion]);

  useEffect(() => {
    if (!open) {
      setSavedKey(null);
      setChildren([]);
      setNewChildName("");
      setEditingChildIndex(null);
    }
    if (open && isUpdateProp && item) {
      setName(item.name || "");
      setBadgeUrl(item.badgeUrl || "");
      setThreshold(item.threshold || "");
      setSavedKey(item.key || item._id);
      setChildren(
        (item.children || []).map((c) => ({
          key: c.key,
          name: c.name,
          parentId: c.parentId,
        }))
      );
      const regionMatch = regionOptions.find((r) => r.value === item.regionId);
      setSelectedRegion(regionMatch || defaultRegion);
      const jpMatch = jobProfileOptions.find((jp) => jp.value === item.jobProfileId);
      setSelectedJobProfile(jpMatch || defaultJobProfile);
    } else if (open && !isUpdateProp) {
      setName("");
      setBadgeUrl("");
      setThreshold("");
      setSelectedRegion(defaultRegion || null);
      setSelectedJobProfile(defaultJobProfile || null);
    }
  }, [open, isUpdateProp, item]);

  const parentId = savedKey || (item && (item.key || item._id));
  const isFormValid = name.trim() !== "" && selectedRegion !== null && selectedJobProfile !== null;

  const handleSave = () => {
    if (threshold !== "" && threshold !== undefined) {
      const parsed = parseFloat(threshold);
      const isValid = /^\d(\.\d{1,2})?$/.test(threshold) && parsed >= 0 && parsed <= 1;
      if (!isValid) {
        Swal.fire({
          icon: "error",
          title: "Threshold non valido",
          text: "Inserire un valore numerico compreso tra 0 e 1 con max 2 decimali (es. 0.75)",
          showConfirmButton: true,
        });
        return;
      }
    }

    const payload = {
      name: name.trim(),
      jobProfileId: selectedJobProfile.value,
      threshold: threshold !== "" ? parseFloat(threshold) : undefined,
      badgeUrl: badgeUrl || undefined,
    };

    if (isEditMode) {
      updateMutation.mutate(
        { id: parentId, input: { name: payload.name, jobProfileId: payload.jobProfileId, badgeUrl: payload.badgeUrl, threshold: payload.threshold } },
        {
          onSuccess: () => {
            const childrenPayload = children.map((c) => ({
              ...(c.key ? { key: c.key } : {}),
              name: c.name,
              parentId: parentId,
            }));
            if (children.length > 0 || (item?.children?.length > 0)) {
              childrenMutation.mutate(
                { dimensionId: parentId, children: childrenPayload },
                {
                  onSuccess: () => {
                    onClose();
                    Swal.fire({
                      icon: "success",
                      title: "Unit of Competence updated!",
                      showConfirmButton: false,
                      timer: 1500,
                    });
                  },
                  onError: (error) => {
                    Swal.fire({
                      icon: "error",
                      title: "Error saving indicators",
                      text: error?.response?.data?.message || error.message,
                      showConfirmButton: true,
                    });
                  },
                }
              );
            } else {
              onClose();
              Swal.fire({
                icon: "success",
                title: "Unit of Competence updated!",
                showConfirmButton: false,
                timer: 1500,
              });
            }
          },
          onError: (error) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: error?.response?.data?.message || error.message,
              showConfirmButton: true,
            });
          },
        }
      );
    } else {
      createMutation.mutate(
        { input: payload },
        {
          onSuccess: (data) => {
            const createdKey = data?.key || data?._id;
            setSavedKey(createdKey);
            Swal.fire({
              icon: "success",
              title: "Unit of Competence created! You can now add indicators.",
              showConfirmButton: false,
              timer: 2000,
            });
          },
          onError: (error) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: error?.response?.data?.message || error.message,
              showConfirmButton: true,
            });
          },
        }
      );
    }
  };

  const handleAddChild = () => {
    if (newChildName.trim() === "") return;
    setChildren([...children, { name: newChildName.trim(), parentId }]);
    setNewChildName("");
  };

  const handleRemoveChild = (index) => {
    setChildren(children.filter((_, i) => i !== index));
  };

  const handleEditChild = (index, newName) => {
    const updated = [...children];
    updated[index] = { ...updated[index], name: newName };
    setChildren(updated);
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference="none"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
      PaperProps={{
        style: {
          padding: "24px",
          maxWidth: "850px",
          width: "100%",
          margin: "auto",
          maxHeight: "85vh",
          overflow: "visible",
        },
      }}
    >
      <Box>
        <Typography variant="h6" gutterBottom>
          {isEditMode ? "Update Unit of Competence" : "New Unit of Competence"}
        </Typography>

        <TextField
          required
          label="Name"
          value={name}
          placeholder="es. UC 1 - Verifica attività"
          onChange={(e) => setName(e.target.value)}
          fullWidth
          margin="normal"
        />

        <Box style={{ marginTop: "8px", marginBottom: "8px" }}>
          <Typography variant="body2" style={{ marginBottom: "4px" }}>
            Region *
          </Typography>
          <Select
            value={selectedRegion}
            onChange={(value) => setSelectedRegion(value)}
            options={regionOptions}
            isClearable
            placeholder="Select Region"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Box>

        <Box style={{ marginTop: "8px", marginBottom: "8px" }}>
          <Typography variant="body2" style={{ marginBottom: "4px" }}>
            Professional Qualification *
          </Typography>
          <Select
            value={selectedJobProfile}
            onChange={(value) => setSelectedJobProfile(value)}
            options={jobProfileOptions}
            isClearable
            isDisabled={!selectedRegion}
            placeholder="Select Professional Qualification"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Box>

        <TextField
          label="Threshold (0.00 – 1.00)"
          value={threshold}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "" || /^(0(\.\d{0,2})?|1(\.0{0,2})?)$/.test(val)) {
              setThreshold(val);
            }
          }}
          fullWidth
          margin="dense"
          placeholder="es. 0.75"
          inputProps={{ maxLength: 4 }}
          helperText="Valore numerico con max 2 decimali compreso tra 0 e 1"
        />

        <Box style={{ marginTop: "8px", marginBottom: "16px" }}>
          <ImageUpload
            value={badgeUrl}
            onChange={(e) => setBadgeUrl(e.target.value)}
            label="Badge Image"
            uploadPath="gams-common/badges"
            uniqueId="uc-badge"
          />
        </Box>

        {isEditMode && parentId && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <Typography variant="subtitle1" gutterBottom>
              Indicators (Children)
            </Typography>

            {children.map((child, index) => (
              <Box
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <TextField
                  value={child.name}
                  onChange={(e) => handleEditChild(index, e.target.value)}
                  disabled={editingChildIndex !== index}
                  fullWidth
                  placeholder="es. UC 1 - 1.1 Attività pratica, UC 1 - 1.2 Teoria"
                  size="small"
                  variant="outlined"
                />
                <IconButton
                  size="small"
                  onClick={() =>
                    setEditingChildIndex(editingChildIndex === index ? null : index)
                  }
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleRemoveChild(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Box style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
              <TextField
                value={newChildName}
                onChange={(e) => setNewChildName(e.target.value)}
                placeholder="New indicator name"
                fullWidth
                size="small"
                variant="outlined"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddChild();
                }}
              />
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddChild}
                disabled={newChildName.trim() === ""}
              >
                Add
              </Button>
            </Box>
          </>
        )}

        <Box style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "24px" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={
              !isFormValid ||
              createMutation.isLoading ||
              updateMutation.isLoading ||
              childrenMutation.isLoading
            }
          >
            {isEditMode ? "Update" : "Save"}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default UnitOfCompetenceModal;
